import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as echarts from 'echarts/core'
import type { EChartsOption } from 'echarts'
import type { ChartPin, Series, SeriesPoint } from '../api/types'
import { useECharts } from '../lib/useECharts'
import { ChartHost } from './ChartHost'
import { PinBar } from './PinBar'
import { PinButton, PinComposer } from './ChartPinUI'
import { PinLayer, type PinTarget } from './PinLayer'
import { formatDateTime, formatAxisLabel } from '../lib/time'
import { escapeHtml } from '../lib/format'
import { shownSeries, withLegendSelection, withPins } from '../lib/chartPins'
import { applyFormat } from '../lib/chartFormat'
import type { Pinning } from '../lib/useChartPins'
import { useChartFormat } from '../lib/useChartFormat'
import { FormatPanel } from './FormatPanel'
import {
  type ZoomEvent, type ZoomPercent, dataExtent, sameSeries, zoomPercent, zoomWindow,
} from '../lib/zoom'

/** Stable per series index: the eight categorical chart colours approved
 *  for the redesign (D1, DESIGN_GUIDELINE section 7), each 3:1 or better on
 *  white. The explorer lets one station show fourteen parameters at once, so
 *  past eight the dash pattern carries the next distinction: eight colours
 *  times three strokes reads as twenty-four series, and every one of them is
 *  named in the legend regardless. */
export const SERIES_COLOUR = ['#0A6A92', '#E07A1F', '#6A4FC2', '#1E8C7E',
  '#C2416B', '#8C7A10', '#3D7FD9', '#58595B']
const SERIES_DASH = ['solid', 'dashed', 'dotted'] as const

export function seriesStyle(index: number): { colour: string; dash: typeof SERIES_DASH[number] } {
  return {
    colour: SERIES_COLOUR[index % SERIES_COLOUR.length]!,
    dash: SERIES_DASH[Math.floor(index / SERIES_COLOUR.length) % SERIES_DASH.length]!,
  }
}

/** Four of the line colours are under 4.5:1 on white, so too light to be
 *  text; where a series name is written in its colour (legend, tooltip)
 *  these darker versions stand in (D1). */
const TEXT_VARIANT: Record<string, string> = {
  '#E07A1F': '#9C4A12', '#1E8C7E': '#16695F', '#8C7A10': '#6B5D0C', '#3D7FD9': '#2F6DC0',
}

export function seriesTextColour(colour: string): string {
  return TEXT_VARIANT[colour] ?? colour
}

interface Props {
  series: Series[]
  height?: number
  /** Told when the reader zooms in or resets, for the time window's badge. */
  onZoomChange?: (zoomed: boolean) => void
  /** The station's pins and how to make them; without it the chart has no
   *  Pin button (the analytics charts, for instance). */
  pinning?: Pinning
  pinContext?: string
  /** Names the chart for the reader's own formatting (`lib/chartFormat`);
   *  without it the chart has no Format button. */
  formatKey?: string
  /** The span the time axis draws, epoch ms, instead of its readings' own.
   *  Explore data's stacked charts all pass the page's one window, so the
   *  same instant sits at the same x on each; left to fit their data, a
   *  chart whose sensor began reporting later would start later. */
  xExtent?: [number, number]
  /** Fixed plot margins instead of margins fitted to the tick labels
   *  (`plotMargins`). Fitted, a chart of 402.5 m starts its plot further
   *  right than one of 0.52 m, and stacked charts stop lining up. */
  alignedGrid?: boolean
  /** Per series, which of the shared series styles (`seriesStyle`) it draws
   *  in; by default its own position. Explore data passes each sensor's
   *  place in the whole selection, so no two sensors on the page share a
   *  colour across its stacked charts. */
  styleIndexes?: number[]
  /** Charts given the same link zoom together: the reader's zoom on one is
   *  applied to the others. Only meaningful with a shared `xExtent`, where
   *  one percentage is one stretch of time on every chart. */
  zoomLink?: string
  /** Every reading drawn as a point on its line, filled in the series
   *  colour, for a sparse series such as a manual sensor read weekly or
   *  monthly: drawn as a line alone, a reading between two gaps has no
   *  neighbour to join and draws nothing at all. The tooltip then goes to
   *  readings only, never to the break in a gap (`untimedBreaks`). Off,
   *  every chart draws as it always has. */
  markers?: boolean
  /** A line under a reading in the tooltip, by series index and epoch ms:
   *  a manual reading's note. Read as the tooltip opens rather than built
   *  into the option, so a new function on each render neither redraws the
   *  chart nor resets the reader's zoom. */
  pointNote?: (seriesIndex: number, at: number) => string | undefined
  /** The tooltip's time, by epoch ms, in place of `formatDateTime`'s
   *  minute: a manual reading may be stamped to the second, and two
   *  readings in one minute must not read as one (`readingTimeLabel`).
   *  Read as the tooltip opens, as `pointNote` is. */
  pointTime?: (at: number) => string
}

/** A marker's size in px (`markers`): clear of the 2 px line it sits on,
 *  small enough that weekly readings over a year stay apart. */
const MARKER_PX = 6

/** Room for one Y axis's tick labels, and for each further axis stacked
 *  outward on the same side (the axes' `offset` of 58 below). */
const AXIS_SIDE = 64
const AXIS_STEP = 58
/** The plot's edge on a side with no axis: clear of the last time label. */
const BARE_SIDE = 24

/** A plot's left and right margins for a fixed grid of `axes` Y axes,
 *  alternating left and right as the chart places them. The same count of
 *  axes always gives the same plot area, whatever the values. */
export function plotMargins(axes: number): { left: number; right: number } {
  const side = (count: number) => (count ? AXIS_SIDE + (count - 1) * AXIS_STEP : BARE_SIDE)
  return { left: side(Math.ceil(axes / 2)), right: side(Math.floor(axes / 2)) }
}

/** One Y axis per axis_group.
 *
 *  Grouping on axis_group and never on unit is rule 4, and this is where the
 *  UI either honours it or does not: water_level (~402 m above datum) and
 *  water_depth (~0.5 m) are both metres. Sharing an axis flattens the depth
 *  series into the baseline and makes it look like a dead sensor.
 */
export function axisGroups(series: Pick<Series, 'axis_group'>[]): string[] {
  return [...new Set(series.map((s) => s.axis_group))]
}

/** Above this many distinct quantities on one chart, the axis names stop
 *  fitting and the plot stops being readable. Rustavi records ten. */
export const CROWDED_AXES = 4

export function isCrowded(series: Pick<Series, 'axis_group'>[]): boolean {
  return axisGroups(series).length > CROWDED_AXES
}

/** How many times the normal cadence a gap must exceed before the line breaks.
 *
 *  Three is loose enough to survive the jitter of a station that logs a few
 *  seconds late and one genuinely missed reading, and tight enough to catch
 *  the multi-hour outages that matter.
 */
export const GAP_FACTOR = 3

/** The station's actual cadence, read off the data rather than its metadata.
 *
 *  The median of the gaps between consecutive readings, which is the polling
 *  interval — so a station whose configured `poll_interval_s` does not match
 *  what it really does is still measured against what it really does.
 *  Null when there is too little to judge from.
 */
export function cadence(times: number[]): number | null {
  if (times.length < 3) return null
  const deltas: number[] = []
  for (let i = 1; i < times.length; i += 1) deltas.push(times[i]! - times[i - 1]!)
  deltas.sort((a, b) => a - b)
  const median = deltas[Math.floor(deltas.length / 2)]!
  return median > 0 ? median : null
}

/** Break the line wherever the station stopped reporting.
 *
 *  An outage arrives as an *absence* of points, not as null values, so
 *  `connectNulls: false` never sees it — it only breaks on a null it is given.
 *  ECharts therefore joins the readings either side, and at Lajanuri that draws
 *  a continuous 39-hour ramp between 2025-12-28 16:25 and 2025-12-30 07:35 as
 *  if the level had been measured all the way across. With `showSymbol: false`
 *  nothing distinguishes that invented segment from a five-minute one.
 *
 *  Inserting an explicit null in the middle of the gap gives connectNulls
 *  something to break on, so the outage reads as what it is: no data.
 */
export function withGaps(points: [number, number | null][],
                         threshold: number | null): [number, number | null][] {
  if (threshold === null) return points
  const out: [number, number | null][] = []
  points.forEach((point, index) => {
    const previous = points[index - 1]
    if (previous && point[0] - previous[0] > threshold) {
      out.push([previous[0] + Math.floor((point[0] - previous[0]) / 2), null])
    }
    out.push(point)
  })
  return out
}

/** A line's breaks with no time of their own, for a chart with markers.
 *
 *  The axis tooltip goes to the point nearest the pointer in time and snaps
 *  the pointer to it, and to ECharts the null `withGaps` puts in the middle
 *  of a gap is a point like any other. Between two monthly readings the
 *  pointer jumped to the empty middle of the gap and the tooltip named a
 *  time nobody read. A break with no time breaks the line just as well,
 *  since a point missing either coordinate breaks it, and it is never the
 *  nearest to anything; a zoomed window keeps it, as dataZoom keeps every
 *  point it cannot place. Only with markers, so every other chart's
 *  tooltip reads as it always has.
 */
export function untimedBreaks(points: [number, number | null][]): [number | null, number | null][] {
  return points.map((point) => (point[1] === null ? [null, null] : point))
}

/** The zoom slider's label: the axis label on one line. */
export function sliderLabel(value: number): string {
  return formatAxisLabel(value).replace('\n', ' ')
}

export function TimeSeriesChart({ series: incoming, height = 460, onZoomChange, pinning,
                                  formatKey, xExtent: extentIn, alignedGrid = false,
                                  styleIndexes: indexesIn, zoomLink, markers = false,
                                  pointNote, pointTime, pinContext }: Props) {
  const series = useStableSeries(incoming)
  // Held while their values are unchanged, as the series are, so a parent
  // building new arrays each render does not reset the reader's zoom.
  const xExtent = useStableNumbers(extentIn)
  const styleIndexes = useStableNumbers(indexesIn)
  const styleOf = useCallback((index: number) => seriesStyle(styleIndexes?.[index] ?? index),
                              [styleIndexes])
  const formatting = useChartFormat(formatKey)
  const [formatOpen, setFormatOpen] = useState(false)
  const [pinMode, setPinMode] = useState(false)
  const [pendingPin, setPendingPin] = useState<PinTarget | null>(null)
  const [pinError, setPinError] = useState<string>()
  const noteOf = useRef(pointNote)
  const timeOf = useRef(pointTime)
  useEffect(() => { noteOf.current = pointNote; timeOf.current = pointTime })
  const dataOption = useMemo<EChartsOption>(() => {
    const groups = axisGroups(series)
    const crowded = groups.length > CROWDED_AXES
    const unitFor = (group: string) =>
      series.find((s) => s.axis_group === group)?.unit ?? ''

    return {
      animation: false,
      // The axis names render at the top of each axis, so the grid needs
      // room for both them and the legend above. At the default top of 48
      // they land on the same line and read as one run-on string:
      // "discharge (m³/s) ○ sq-rustavi · Discharge ... depth (m)".
      // Aligned, the bottom keeps the room containLabel would have given the
      // two-line time labels (formatAxisLabel), clear of the slider.
      grid: alignedGrid
        ? { ...plotMargins(groups.length), top: 72, bottom: 76 + TIME_LABEL_ROOM, containLabel: false }
        : { left: 8, right: 8, top: 72, bottom: 76, containLabel: true },
      legend: {
        type: 'scroll',
        top: 4,
        data: series.map((item, index) => ({
          name: seriesLabel(item),
          textStyle: { color: seriesTextColour(styleOf(index).colour) },
        })),
      },
      tooltip: {
        trigger: 'axis',
        // Kept inside the chart with markers, as AnalysisChart's are: each
        // row then names its series and may carry a note up to 260 px, and
        // at phone width ECharts flips the box left of the pointer and past
        // the card's edge, where the chart host's overflow cuts it off.
        // Confining moves the box but cannot narrow it, and ECharts never
        // wraps a tooltip, so a sensor's name of up to 100 characters ran
        // 700 px wide on a 310 px chart, its value cut off with it. With
        // markers the name wraps at the chart's width instead (the value
        // never does, below). Other charts' tooltips stay as they always
        // have been.
        ...(markers ? { confine: true, extraCssText: 'white-space:normal;overflow-wrap:anywhere' } : {}),
        axisPointer: { type: 'cross' },
        formatter: (params) => {
          const points = Array.isArray(params) ? params : [params]
          const first = points[0]
          if (!first) return ''
          const stamp = (first.value as [number, number])[0]
          // A pinned stretch's overlay is not a reading: never listed.
          const rows = points.filter((point) => (point.seriesIndex ?? 0) < series.length).map((point) => {
            const [at, value] = point.value as [number, number | null]
            const index = point.seriesIndex ?? 0
            const unit = series[index]?.unit ?? ''
            const colour = seriesTextColour(styleOf(index).colour)
            const note = value === null ? undefined : noteOf.current?.(index, at)
            // The stored value, not a rounded one. This is where someone
            // reads a number off the chart to write down, so it shows
            // exactly what the database holds.
            return `<div style="display:flex;gap:8px;justify-content:space-between">
              <span style="color:${colour}">${point.marker} ${escapeHtml(point.seriesName ?? '')}</span>
              <b${markers ? ' style="white-space:nowrap"' : ''}>${value === null ? '—' : `${value} ${escapeHtml(unit)}`}</b>
            </div>` + (note
              ? `<div style="max-width:260px;white-space:normal;margin-top:2px">${escapeHtml(note)}</div>`
              : '')
          })
          // Station-local, like every other timestamp in the app. ECharts
          // would otherwise render this in the browser's zone.
          const time = timeOf.current
          return `<div style="font-weight:600;margin-bottom:4px">
            ${time ? escapeHtml(time(stamp)) : formatDateTime(new Date(stamp))}</div>${rows.join('')}`
        },
      },
      xAxis: {
        type: 'time',
        ...(xExtent ? { min: xExtent[0], max: xExtent[1] } : {}),
        axisLabel: {
          formatter: (value: number) => formatAxisLabel(value),
          hideOverlap: true,
        },
      },
      yAxis: groups.map((group, index) => ({
        type: 'value' as const,
        // Past a handful of groups the names overlap into an unreadable band
        // across the top — five axes a side, each labelled. The tick values
        // still give the scale and the legend still names every series, so
        // dropping the names loses nothing but the collision. Selecting every
        // measurement a station records makes this the common case.
        name: crowded
          ? ''
          : `${group}${unitFor(group) ? ` (${unitFor(group)})` : ''}`,
        nameTextStyle: { align: index % 2 === 0 ? 'left' : 'right' },
        // Clear of the legend above rather than tucked against it.
        nameGap: 14,
        position: index % 2 === 0 ? 'left' as const : 'right' as const,
        // Third axis onward stacks outward from the plot rather than
        // overlapping the second.
        offset: Math.floor(index / 2) * 58,
        scale: true,
        splitLine: { show: index === 0 },
      })),
      dataZoom: [
        { type: 'inside' as const, throttle: 50 },
        // In the palette rather than ECharts' blue-grey: a Hydro 25 window
        // over a Chrome 50 track, Slate handles and preview line.
        {
          type: 'slider' as const, height: 24, bottom: 16,
          borderColor: '#DCDDDE',
          backgroundColor: '#FFFFFF',
          fillerColor: 'rgba(218, 243, 252, 0.7)',
          dataBackground: {
            lineStyle: { color: '#A8A9AC', width: 1 },
            areaStyle: { color: '#E7E7E8', opacity: 1 },
          },
          selectedDataBackground: {
            lineStyle: { color: '#58595B', width: 1 },
            areaStyle: { color: '#DAF3FC', opacity: 1 },
          },
          handleStyle: { color: '#FFFFFF', borderColor: '#58595B' },
          moveHandleStyle: { color: '#A8A9AC' },
          textStyle: { color: '#58595B' },
          labelFormatter: sliderLabel,
        },
      ],
      series: series.map((item, index) => ({
        name: seriesLabel(item),
        type: 'line' as const,
        yAxisIndex: groups.indexOf(item.axis_group),
        showSymbol: markers,
        // Filled, where ECharts' default is a white circle ringed in the
        // colour: a ring is a pin here (chartPins), and a reading is not.
        ...(markers ? { symbol: 'circle', symbolSize: MARKER_PX } : {}),
        // Deliberately NOT sampled. lttb would draw a representative subset
        // when there are more points than pixels, which reads as the data and
        // is not: on a tipping-bucket counter it can drop the step just
        // before a reset. Every point the API returned is drawn. If that is
        // more than the series limit, the API says so and the header shows
        // `truncated` — a stated gap rather than a silently smoothed line.
        large: true,
        largeThreshold: 2000,
        // Counters and statuses hold their value until the next reading;
        // instantaneous readings genuinely vary between samples.
        //
        // This matters most on an event-driven counter. Shaori precipitation
        // emits a row per 0.2 mm tip, so after the last tip of the day there
        // is nothing until the 00:01 reset — and a straight line between them
        // draws a slow decline from 1.8 mm to zero, asserting intermediate
        // values that were never measured and never occurred. Stepping
        // asserts only what the reading says: it read 1.8 until it read 0.
        step: stepped(item) ? ('end' as const) : undefined,
        lineStyle: { width: 2, type: styleOf(index).dash },
        itemStyle: { color: styleOf(index).colour },
        connectNulls: false,
        data: markers ? untimedBreaks(gapped(item)) : gapped(item),
      })),
    }
  }, [series, xExtent, alignedGrid, styleOf, markers])

  const pins = pinning?.pins ?? NO_PINS
  const format = formatting.format
  // The reader's legend choices, carried into every new option so a hidden
  // line stays hidden when a pin is made or the format changes.
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean>>({})
  const option = useMemo(
    () => withLegendSelection(
      withPins(applyFormat(dataOption, format, series), series, pins, pinMode, gapped),
      legendSelected),
    [dataOption, format, series, pins, pinMode, legendSelected])
  const shown = useMemo(() => shownSeries(series, legendSelected, seriesLabel),
                        [series, legendSelected])

  const container = useECharts(option, [option])
  // True once useECharts has made the instance. Its init effect is declared
  // above, so it has run by the time this one does; children's effects run
  // before both, so the pin layer would otherwise first look for the chart
  // before it exists and, with its pins already loaded, never look again.
  const [ready, setReady] = useState(false)
  useEffect(() => { setReady(true) }, [])
  const zoom = useZoomWindow(container, dataOption, series, xExtent, zoomLink)
  useEffect(() => {
    const chart = container.current ? echarts.getInstanceByDom(container.current) : undefined
    if (!chart) return
    const onLegend = (event: unknown) => {
      setLegendSelected({ ...((event as { selected?: Record<string, boolean> }).selected ?? {}) })
    }
    chart.on('legendselectchanged', onLegend)
    return () => { if (!chart.isDisposed()) chart.off('legendselectchanged', onLegend) }
  }, [container, ready])
  // A new option replaces the old one whole (useECharts, notMerge), which
  // resets the zoom. That is right for new data and wrong for a pin made or
  // Pin mode turned on, so the reader's zoom is put back unless the data
  // itself changed.
  const appliedData = useRef<EChartsOption | null>(null)
  useEffect(() => {
    const chart = container.current ? echarts.getInstanceByDom(container.current) : undefined
    const kept = zoom.last.current
    if (appliedData.current === dataOption) {
      if (chart && (kept.start > 0 || kept.end < 100)) {
        chart.dispatchAction({ type: 'dataZoom', start: kept.start, end: kept.end })
      }
    } else {
      zoom.last.current = { start: 0, end: 100 }
    }
    appliedData.current = dataOption
  }, [option, dataOption, container, zoom.last])

  const look = useCallback((parameter: string) => {
    const index = series.findIndex((s) => s.parameter === parameter)
    if (index < 0) return null
    const colour = styleOf(index).colour
    return { name: [pinContext, series[index]!.display_name].filter(Boolean).join(' · '), colour, text: seriesTextColour(colour),
             unit: series[index]!.unit }
  }, [series, styleOf, pinContext])

  const create = pinning?.create
  // A click while a note is being written keeps that note and its reading:
  // only saving or cancelling lets the next reading be chosen.
  const onPin = useCallback((target: PinTarget) => {
    setPinError(undefined)
    setPendingPin((composing) => composing ?? target)
  }, [])
  const remove = pinning?.remove
  const onRemove = useCallback((pin: ChartPin) => {
    if (!remove) return
    setPinError(undefined)
    return remove(pin)
  }, [remove])
  const leavePinMode = useCallback(() => { setPinMode(false); setPendingPin(null) }, [])
  const pendingLine = series.find(line => line.parameter === pendingPin?.parameter)
  // The line went (a sensor unticked, a new station): so does its pin in the making.
  useEffect(() => { if (pendingPin && !pendingLine) setPendingPin(null) }, [pendingPin, pendingLine])
  const pendingValue = pendingLine?.points.find(point => point[0] === pendingPin?.ts)?.[1]
  const pendingEnd = pendingLine?.points.find(point => point[0] === pendingPin?.ts_end)?.[1]
  const zoomed = zoom.window !== null
  // Reported back on every change and withdrawn when the chart goes (a new
  // station, a sensor unticked), so the time window never keeps calling
  // itself zoomed for a chart no longer on screen.
  useEffect(() => {
    if (!onZoomChange) return
    onZoomChange(zoomed)
    return () => onZoomChange(false)
  }, [zoomed, onZoomChange])

  return (
    <>
      {(zoom.window || pinning || formatKey) && (
        <div className="zoom-bar">
          {zoom.window && (
            <>
              <button type="button" className="button accent" onClick={zoom.reset}>Reset zoom</button>
              <span className="muted">
                Zoomed: <span className="mono">{formatDateTime(zoom.window[0])}</span> to{' '}
                <span className="mono">{formatDateTime(zoom.window[1])}</span>
              </span>
            </>
          )}
          {formatKey && (
            <button type="button" className="button chart-tool" aria-pressed={formatOpen}
                    onClick={() => setFormatOpen((open) => !open)}>
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 6h9M19 6h1M4 12h3M13 12h7M4 18h11M21 18h-1" />
                <circle cx="16" cy="6" r="2.5" /><circle cx="10" cy="12" r="2.5" />
                <circle cx="18" cy="18" r="2.5" />
              </svg>
              Format
            </button>
          )}
          {pinning && (
            <PinButton active={pinMode} onClick={() => { setPendingPin(null); setPinMode(on => !on) }} />
          )}
        </div>
      )}
      {formatKey && formatOpen && (
        <FormatPanel series={series} format={format} onChange={formatting.change}
                     onReset={formatting.reset} onDone={() => setFormatOpen(false)}
                     look={(index) => {
                       const colour = styleOf(index).colour
                       return { colour, text: seriesTextColour(colour) }
                     }} />
      )}
      {formatKey && formatting.error && (
        <p className="notice notice-warn" role="alert">{formatting.error}</p>
      )}
      {pinning && pinMode && !pendingPin && <PinBar stretches onDone={leavePinMode} />}
      {pinning && pendingPin && pendingLine && <PinComposer key={`${pendingPin.parameter}:${pendingPin.ts}:${pendingPin.ts_end}`} reading={{label:[pinContext,pendingLine.display_name].filter(Boolean).join(' · '),
        time:`${formatDateTime(new Date(pendingPin.ts))}${pendingPin.ts_end ? ` – ${formatDateTime(new Date(pendingPin.ts_end))}` : ''} · UTC+4`,
        value:`${pendingValue ?? '—'}${pendingPin.ts_end ? ` → ${pendingEnd ?? '—'}` : ''} ${pendingLine.unit}`}}
        onCancel={() => setPendingPin(null)} onSave={async note => { await create?.(pendingPin, note); setPendingPin(null) }} />}
      {pinning && (pinError || pinning.error) && (
        <p className="notice notice-warn" role="alert">{pinError ?? pinning.error}</p>
      )}
      <ChartHost container={container} height={height} pinMode={pinMode}>
        {pinning && (
          <PinLayer container={container} ready={ready} series={series} shown={shown} pins={pins}
                    pinMode={pinMode}
                    look={look} canRemove={pinning.canRemove} onPin={onPin}
                    onMiss={setPinError} onRemove={onRemove} />
        )}
      </ChartHost>
    </>
  )
}

const NO_PINS: ChartPin[] = []

/** What the two-line time labels under an aligned plot need: two 12 px
 *  lines and the label margin, which containLabel otherwise measures. */
const TIME_LABEL_ROOM = 34

/** The same array as last time while its numbers are unchanged. */
function useStableNumbers<T extends readonly number[] | undefined>(value: T): T {
  const kept = useRef(value)
  if (kept.current !== value && !sameNumbers(kept.current, value)) kept.current = value
  return kept.current
}

export function sameNumbers(a: readonly number[] | undefined, b: readonly number[] | undefined): boolean {
  if (!a || !b) return a === b
  return a.length === b.length && a.every((n, i) => n === b[i])
}

/** Live charts by zoom link (`zoomLink`): Explore data's stacked charts. */
const zoomLinks = new Map<string, Set<echarts.ECharts>>()

/** The series as last drawn while what they draw is unchanged
 *  (`sameSeries`), so a parent that rebuilds the list on every render does
 *  not hand the chart a new option, which would reset the reader's zoom. */
function useStableSeries(series: Series[]): Series[] {
  const kept = useRef(series)
  if (kept.current !== series && !sameSeries(kept.current, series)) kept.current = series
  return kept.current
}

/** What part of the data the reader has zoomed to, if any, and how to go
 *  back. Read off ECharts' own `datazoom` event through the instance on the
 *  container, so both dataZoom controls (wheel and slider) report here, and
 *  off the event's own payload (lib/zoom.ts); the window is the data's own
 *  time extent cut at the zoom's percentages, or the axis's own span when
 *  it is fixed (`xExtent`), since ECharts then reads the percentages
 *  against that span. A new option (new data) starts unzoomed, since
 *  `notMerge` resets it.
 *
 *  With a link, the reader's own zoom is passed to the other charts of the
 *  link as a marked action; a marked zoom arriving here is shown but never
 *  passed on, so two charts do not echo each other's zoom back and forth. */
function useZoomWindow(container: React.RefObject<HTMLDivElement>, dataOption: EChartsOption,
                       series: Series[], xExtent: [number, number] | undefined,
                       link: string | undefined) {
  const [shown, setShown] = useState<[Date, Date] | null>(null)
  // The zoom as last reported, for putting back after a new option.
  const last = useRef<ZoomPercent>({ start: 0, end: 100 })

  const extent = useMemo(() => xExtent ?? dataExtent(series), [series, xExtent])

  useEffect(() => { setShown(null) }, [dataOption])

  useEffect(() => {
    const chart = container.current ? echarts.getInstanceByDom(container.current) : undefined
    if (!chart) return
    let members: Set<echarts.ECharts> | undefined
    if (link) {
      members = zoomLinks.get(link) ?? new Set()
      zoomLinks.set(link, members)
      members.add(chart)
    }
    const onZoom = (event: unknown) => {
      last.current = zoomPercent(event as ZoomEvent)
      setShown(zoomWindow(extent, last.current))
      if (!members || (event as { linked?: boolean }).linked) return
      for (const other of members) {
        if (other !== chart && !other.isDisposed()) {
          other.dispatchAction({ type: 'dataZoom', ...last.current, linked: true })
        }
      }
    }
    chart.on('datazoom', onZoom)
    return () => {
      if (!chart.isDisposed()) chart.off('datazoom', onZoom)
      if (link && members) {
        members.delete(chart)
        if (!members.size) zoomLinks.delete(link)
      }
    }
  }, [container, extent, link])

  const reset = () => {
    const chart = container.current ? echarts.getInstanceByDom(container.current) : undefined
    chart?.dispatchAction({ type: 'dataZoom', start: 0, end: 100 })
  }
  return { window: shown, reset, last }
}

function seriesLabel(series: Series): string {
  return `${series.station} · ${series.display_name}`
}

function stepped(series: Pick<Series, 'kind'>): boolean {
  return series.kind === 'status' || series.kind.startsWith('counter')
}

/** Points in chart coordinates, with outages broken.
 *
 *  Only for series drawn as a plain line. A stepped series already refuses to
 *  invent intermediate values — it asserts "it read 1.8 until it read 0" — and
 *  breaking it would lose exactly the reading the step was drawn to show: an
 *  event-driven tipping bucket is silent between tips by design, so its normal
 *  cadence is a gap and every day would shatter into fragments.
 */
function gapped(series: Series, subset: SeriesPoint[] = series.points): [number, number | null][] {
  const toChart = (list: SeriesPoint[]): [number, number | null][] =>
    list.map((point) => [new Date(point[0]).getTime(), point[1]])
  const points = toChart(subset)
  if (stepped(series)) return points
  // The series' own cadence, even for a pinned stretch's few points, so the
  // overlay breaks exactly where its line does.
  const median = cadence(series.points.map((point) => new Date(point[0]).getTime()))
  return withGaps(points, median === null ? null : median * GAP_FACTOR)
}
